import { Component } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Table } from "../../shared/table/table";
import { Popup } from "../../../core/shared/components/popup/popup";
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { take } from 'rxjs';
import { AppIcons } from '../../../core/icons/fa-icons';
import { DynamicFieldConfig } from '../../../core/shared/dynamic-form/form-config.model';
import { PKL_GRP } from '../../../core/shared/models/picklist.model';
import { PickListService } from '../../../core/shared/services/picklist.service';
import { TableAction, TableColumn } from '../../shared/models/table-models';
import { Setup } from '../shared/service/setup';

@Component({
  selector: 'app-financial-data-setup',
  imports: [TranslatePipe, Table, Popup],
  templateUrl: './financial-data-setup.html',
  styleUrl: './financial-data-setup.css',
})
export class FinancialDataSetup {
icons = AppIcons;

  list: any[] = [];
  columns: TableColumn[] = [];

  showPopup = false;
  isEditMode = false;
  isViewMode = false;
  formData: any = {};

  fields: DynamicFieldConfig[] = [];

  showDeletePopup = false;
  deleteMessage = '';
  selectedRow: any = null;

  constructor(
    private translate: TranslateService,
    private setup: Setup,
    private toastr: ToastrService,
    private picklist: PickListService,
      private router: Router

  ) {}

  // ================= INIT =================
  ngOnInit(): void {
    this.loadPicklists();
  }

  // ================= PICKLIST =================
  loadPicklists() {
    this.picklist.loadCodes([PKL_GRP.COMMON_STATUS]);

    this.picklist.picklists$.pipe(take(1)).subscribe(() => {
      this.buildFields();
      this.buildColumns();
      this.loadData();
    });
  }

  // ================= FORM =================
  buildFields() {
    this.fields = [
      {
        key: 'geoSeq',
        label: 'SETUP.ID',
        type: 'text',
        hidden: true,
      },
      {
        key: 'name',
        label: 'COMMON.NAME',
        type: 'text',
        validations: { required: true },
      },
      {
        key: 'status',
        label: 'COMMON.STATUS',
        type: 'select',
        options$: this.picklist.getOptionsSync(PKL_GRP.COMMON_STATUS),
        validations: { required: true },
      },
      {
        key: 'description',
        label: 'COMMON.DESCRIPTION',
        type: 'textarea',
      },
    ];
  }

  // ================= TABLE =================
buildColumns() {
  this.columns = [
    { key: 'geoSeq', label: 'SETUP.ID' },

    {
      key: 'name',
      label: 'COMMON.NAME',
      type: 'link',
      onClick: (row: any) => this.goToFinancialValue(row)
    },

    { key: 'description', label: 'COMMON.DESCRIPTION' },

    {
      key: 'status',
      label: 'COMMON.STATUS',
      type: 'badge',
      map: {
        706: 'COMMON.ACTIVE',
        707: 'COMMON.INACTIVE'
      },
      badgeMap: {
        706: 'status-active',
        707: 'status-inactive'
      }
    }
  ];
}
goToFinancialValue(row: any) {
  this.router.navigate(['/financial-data-value', row.geoSeq]);
}
  // label mapping
  getStatusMap(): Record<number, string> {
    const options = this.picklist.getOptionsSync(PKL_GRP.COMMON_STATUS);
    const map: Record<number, string> = {};

    options.forEach((o) => {
      map[o.value] = o.label;
    });

    return map;
  }

  // ✅ dynamic color mapping
  getStatusBadgeMap(): Record<number, string> {
    const options = this.picklist.getOptionsSync(PKL_GRP.COMMON_STATUS);
    const map: Record<number, string> = {};

    options.forEach((o) => {
      const label = o.label?.toLowerCase();

      if (label === 'active') {
        map[o.value] = 'status-active';
      } else if (label === 'inactive') {
        map[o.value] = 'status-inactive';
      } else if (label === 'restructured') {
        map[o.value] = 'status-restructured';
      } else {
        map[o.value] = 'status-default';
      }
    });

    return map;
  }

  // ================= ACTIONS =================
  actions: TableAction[] = [
    {
      icon: this.icons.edit,
      tooltip: 'COMMON.EDIT',
      class: 'action-edit',
      onClick: (row) => this.openPopup(row),
    },
    {
      icon: this.icons.view,
      tooltip: 'COMMON.VIEW',
      class: 'action-view',
      onClick: (row) => this.openPopup(row, true),
    },
    {
      icon: this.icons.delete,
      tooltip: 'COMMON.DELETE',
      class: 'action-delete',
      onClick: (row) => this.openDeletePopup(row),
    },
  ];

  // ================= DELETE CONFIRM =================
  openDeletePopup(row: any) {
    this.selectedRow = row;
    this.deleteMessage = this.translate.instant('SETUP.CONFIRM_DELETE_NAME', { name: row.name });
    this.showDeletePopup = true;
  }

  confirmDelete(): void {
    if (!this.selectedRow) return;
    this.delete(this.selectedRow);
    this.selectedRow = null;
  }

  // ================= LOAD =================
loadData() {
  this.setup.getAllFinancialTypes().subscribe({
    next: (res: any[]) => {
      this.list = (res || []).map(item => ({
        geoSeq: item.fncTypSeq,
        name: item.typNm,
        description: item.dscr,
        status: item.actvFlg ? 706 : 707
      }));
    },
    error: () => this.toastr.error(this.translate.instant('SETUP.FIN_DATA.LOAD_TYPES_FAILED'))
  });
}
onCellClick(event: any) {
  if (event.column.key === 'name') {
    this.router.navigate(['/financial-data-value', event.row.geoSeq]);
  }
}


  // ================= POPUP =================
  openPopup(data?: any, viewMode: boolean = false) {
    this.showPopup = true;
    this.isViewMode = viewMode;

    if (data) {
      this.isEditMode = true;

      this.formData = {
        geoSeq: data.geoSeq ?? data.countrySeq,
        name: data.name,
        description: data.description,
        status: data.status,
      };
    } else {
      this.isEditMode = false;

      this.formData = {
        geoSeq: null,
        status: this.getDefaultStatus(),
      };
    }
  }

  getDefaultStatus(): number {
    const options = this.picklist.getOptionsSync(PKL_GRP.COMMON_STATUS);
    return options?.[0]?.value || 15;
  }

  // ================= SAVE =================
handleSave = (form: any) => {
  const payload = {
    fncTypSeq: form.geoSeq ?? null,
    typNm: form.name,
    dscr: form.description,
    actvFlg: Number(form.status) === 706,
    delFlg: 0
  };

  return this.setup.saveFinancialType(payload).subscribe({
    next: () => {
      this.toastr.success(this.translate.instant(this.isEditMode ? 'SETUP.FIN_DATA.TYPE_UPDATED' : 'SETUP.FIN_DATA.TYPE_CREATED'));
      this.showPopup = false;
      this.loadData();
    },
    error: () => this.toastr.error(this.translate.instant('SETUP.SAVE_FAILED'))
  });
};

  // ================= DELETE =================
delete(row: any) {
  const payload = {
    fncTypSeq: row.geoSeq,
    typNm: row.name,
    dscr: row.description,
    actvFlg: Number(row.status) === 706,
    delFlg: 1
  };

  this.setup.saveFinancialType(payload).subscribe({
    next: () => {
      this.toastr.success(this.translate.instant('SETUP.DELETED_SUCCESSFULLY'));
      this.loadData();
    },
    error: () => this.toastr.error(this.translate.instant('SETUP.DELETE_FAILED'))
  });
}
}

